import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue, get, off } from 'firebase/database';
import { db } from '../../../configs/Firebase';
import { useSelector } from 'react-redux';
import MentionAutocomplete from './MentionAutocomplete';
import { getPlainTextFromEditor, extractMentionsFromEditor, isMentionBoundary } from './mentionUtils';
import style from './CellCommentsSidebar.module.css';

const TipTapCommentInput = ({ 
  value = '', 
  onChange, 
  onMentionsChange,
  placeholder = 'Add a comment...',
  orgId 
}) => {
  const [users, setUsers] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteQuery, setAutocompleteQuery] = useState('');
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const editorRef = useRef(null);
  const autocompleteContainerRef = useRef(null);
  const suggestionStateRef = useRef(null);

  // Load users for autocomplete
  useEffect(() => {
    if (!orgId) return;

    const usersRef = ref(db, `userData/${orgId}/users/`);
    
    const unsubscribe = onValue(usersRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const usersList = [];
        for (let i in data) {
          if (data[i].id) {
            try {
              const profileSnapshot = await get(ref(db, `userData/${data[i].id}/profile`));
              const profile = profileSnapshot.val() || {};
              usersList.push({
                id: data[i].id,
                name: profile.displayName || data[i].name || "",
                email: profile.email || data[i].email || "",
                photoURL: profile.microsoftPhotoURL || null,
              });
            } catch (error) {
              console.error(`Error loading profile for user ${data[i].id}:`, error);
              usersList.push({
                id: data[i].id,
                name: data[i].name || "",
                email: data[i].email || "",
                photoURL: null,
              });
            }
          }
        }
        setUsers(usersList);
      }
    });

    return () => {
      off(usersRef);
      unsubscribe();
    };
  }, [orgId]);

  // Filter users based on query
  const getFilteredUsers = useCallback((query) => {
    if (!query) return users.slice(0, 10);
    
    const filtered = users.filter(user => {
      const nameMatch = user.name.toLowerCase().includes(query.toLowerCase());
      const emailMatch = user.email.toLowerCase().includes(query.toLowerCase());
      return nameMatch || emailMatch;
    });
    
    return filtered.slice(0, 10);
  }, [users]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: style.mentionInline,
        },
        suggestion: {
          char: '@',
          allowedCharacters: /^[\w\s]*$/,
          items: ({ query }) => {
            return getFilteredUsers(query);
          },
          render: () => {
            return {
              onStart: (props) => {
                setShowAutocomplete(true);
                setAutocompleteQuery(props.query);
                setSelectedUserIndex(0);
                // Store the command function and other necessary props
                suggestionStateRef.current = {
                  command: props.command,
                  range: props.range,
                  query: props.query,
                };
                
                // Position the autocomplete dropdown
                updateAutocompletePosition(props);
              },
              onUpdate: (props) => {
                setAutocompleteQuery(props.query);
                setSelectedUserIndex(0);
                // Update the stored props
                suggestionStateRef.current = {
                  command: props.command,
                  range: props.range,
                  query: props.query,
                };
                
                // Update position
                updateAutocompletePosition(props);
              },
              onKeyDown: (props) => {
                const filtered = getFilteredUsers(props.query);
                
                if (props.event.key === 'ArrowUp') {
                  setSelectedUserIndex(prev => prev > 0 ? prev - 1 : filtered.length - 1);
                  return true;
                }
                if (props.event.key === 'ArrowDown') {
                  setSelectedUserIndex(prev => prev < filtered.length - 1 ? prev + 1 : 0);
                  return true;
                }
                if (props.event.key === 'Enter' || props.event.key === 'Tab') {
                  if (filtered[selectedUserIndex]) {
                    const selectedUser = filtered[selectedUserIndex];
                    if (props.command) {
                      props.command({
                        id: selectedUser.id,
                        label: selectedUser.name,
                      });
                    } else if (props.selectItem) {
                      props.selectItem(selectedUser);
                    }
                    return true;
                  }
                }
                if (props.event.key === 'Escape') {
                  setShowAutocomplete(false);
                  return true;
                }
                return false;
              },
              onExit: () => {
                setShowAutocomplete(false);
                setAutocompleteQuery('');
                setSelectedUserIndex(0);
                suggestionStateRef.current = null;
              },
            };
          },
        },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const plainText = getPlainTextFromEditor(editor);
      const mentions = extractMentionsFromEditor(editor);
      
      if (onChange) {
        onChange(plainText);
      }
      
      if (onMentionsChange) {
        onMentionsChange(mentions);
      }
    },
    editorProps: {
      attributes: {
        class: style.commentInput,
        'data-placeholder': placeholder,
      },
    },
  });

  // Update autocomplete position
  const updateAutocompletePosition = (props) => {
    if (props.range) {
      const coords = props.clientRect();
      if (coords && editorRef.current) {
        const containerRect = editorRef.current.getBoundingClientRect();
        setAutocompletePosition({
          top: coords.bottom - containerRect.top + 4, // Position below the @ symbol
          left: coords.left - containerRect.left,
        });
      }
    }
  };

  // Update editor content when value prop changes
  useEffect(() => {
    if (!editor) return;
    
    const currentText = getPlainTextFromEditor(editor);
    if (value !== currentText && value !== undefined) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  // Handle atomic deletion
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      // Handle backspace/delete at mention boundaries
      if (event.key === 'Backspace' || event.key === 'Delete') {
        const boundary = isMentionBoundary(editor);
        if (boundary && boundary.isBoundary) {
          // Delete the entire mention
          const { mention } = boundary;
          const { state, dispatch } = editor.view;
          const { tr } = state;
          
          tr.delete(mention.start, mention.end);
          dispatch(tr);
          
          event.preventDefault();
          return true;
        }
      }
      
      // Prevent typing inside mentions
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const boundary = isMentionBoundary(editor);
        if (boundary && boundary.mention) {
          // Move cursor to after the mention
          const { mention } = boundary;
          const { state, dispatch } = editor.view;
          const { tr } = state;
          
          tr.setSelection(state.tr.doc.resolve(mention.end));
          dispatch(tr);
        }
      }
      
      return false;
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor]);

  // Handle user selection from autocomplete
  const handleUserSelect = useCallback((user) => {
    if (!editor || !suggestionStateRef.current || !suggestionStateRef.current.command) return;
    
    // Use the command function from suggestion props to insert the mention
    suggestionStateRef.current.command({
      id: user.id,
      label: user.name,
    });
    
    setShowAutocomplete(false);
    suggestionStateRef.current = null;
  }, [editor]);

  // Filter users for autocomplete display
  const filteredUsers = getFilteredUsers(autocompleteQuery);

  if (!editor) {
    return null;
  }

  return (
    <div className={style.commentInputContainer} ref={editorRef}>
      <EditorContent editor={editor} />
      {showAutocomplete && filteredUsers.length > 0 && (
        <div
          ref={autocompleteContainerRef}
          className={style.autocompleteWrapper}
          style={{
            position: 'absolute',
            top: `${autocompletePosition.top}px`,
            left: `${autocompletePosition.left}px`,
            zIndex: 1000,
            minWidth: '250px',
            width: 'auto',
          }}
        >
          <MentionAutocomplete
            text={autocompleteQuery}
            cursorPosition={autocompleteQuery.length}
            onSelect={handleUserSelect}
            onClose={() => {
              setShowAutocomplete(false);
              if (suggestionStateRef.current) {
                suggestionStateRef.current.onExit();
              }
            }}
            orgId={orgId}
            users={filteredUsers}
            selectedIndex={selectedUserIndex}
            onIndexChange={setSelectedUserIndex}
          />
        </div>
      )}
    </div>
  );
};

export default TipTapCommentInput;
